'use client'

import { Tooltip as BaseTooltip } from '@base-ui/react/tooltip'
import { useEffect, useState } from 'react'

const TOOLTIP_POPUP_CLASS =
  'pointer-events-none max-w-64 origin-[var(--transform-origin)] rounded-control glass-menu px-2.5 py-1.5 text-center text-c13 leading-[1.4] font-medium break-keep text-foreground transition-[opacity,scale] duration-150 ease-out data-[starting-style]:scale-95 data-[starting-style]:opacity-0 data-[ending-style]:scale-95 data-[ending-style]:opacity-0'

type Side = 'top' | 'bottom' | 'left' | 'right'
type Tip = { anchor: Element; text: string; side: Side }
type Source = 'hover' | 'focus'

const TARGET =
  '[title]:not(iframe),button,a,[role="button"],[role="radio"],[role="switch"],[role="tab"],[role="checkbox"],[role="img"][aria-label],span[aria-label]'
const OPEN_DELAY = 350
const WARM_WINDOW = 400
const WATCH_INTERVAL = 250
const ICON_MAX_WIDTH = 200
const ICON_MAX_HEIGHT = 64

function visibleText(el: Element) {
  let text = ''
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT)
  while (walker.nextNode()) {
    const parent = walker.currentNode.parentElement
    if (parent && !parent.closest('.sr-only') && parent.getClientRects().length)
      text += walker.currentNode.textContent ?? ''
  }
  return text.trim()
}

function tipOf(el: Element) {
  if (el.getAttribute('aria-expanded') === 'true') return ''
  const title = el.getAttribute('title')?.trim()
  const text = visibleText(el)
  if (title)
    return title === text && el.scrollWidth <= el.clientWidth ? '' : title
  const { width, height } = el.getBoundingClientRect()
  if (width > ICON_MAX_WIDTH || height > ICON_MAX_HEIGHT || /\p{L}/u.test(text))
    return ''
  return (
    el.getAttribute('aria-label')?.trim() ||
    el.querySelector('.sr-only')?.textContent?.trim() ||
    ''
  )
}

/**
 * 앱 전체 호버 · 키보드 포커스 툴팁 한 곳. 버튼마다 감싸지 않고 문서에서 이벤트를 받아 띄운다.
 * 문구 순서: title → 글자 없는 작은 요소의 aria-label → sr-only. 위치는 기본 위, data-tip-side 로 바꾼다. 터치에서는 뜨지 않는다.
 */
export default function TooltipHost() {
  const [tip, setTip] = useState<Tip | null>(null)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    let current: Element | null = null
    let source: Source = 'hover'
    let shown = false
    let closedAt = 0
    let openTimer: ReturnType<typeof setTimeout> | undefined
    let watch: ReturnType<typeof setInterval> | undefined
    let restoreTitle: (() => void) | undefined

    const hide = () => {
      clearTimeout(openTimer)
      if (!shown) return
      shown = false
      closedAt = performance.now()
      setOpen(false)
    }
    const clear = () => {
      clearTimeout(openTimer)
      clearInterval(watch)
      restoreTitle?.()
      restoreTitle = undefined
      current = null
    }
    const release = () => {
      hide()
      clear()
    }
    const begin = (el: Element, text: string, from: Source) => {
      clear()
      current = el
      source = from
      const title = el.getAttribute('title')
      if (title !== null) {
        el.removeAttribute('title')
        restoreTitle = () => {
          if (!el.hasAttribute('title')) el.setAttribute('title', title)
        }
      }
      const next: Tip = {
        anchor: el,
        text,
        side: (el.getAttribute('data-tip-side') as Side | null) ?? 'top',
      }
      const show = () => {
        shown = true
        setTip(next)
        setOpen(true)
      }
      if (shown) show()
      else
        openTimer = setTimeout(
          show,
          performance.now() - closedAt < WARM_WINDOW ? 0 : OPEN_DELAY
        )
      watch = setInterval(() => {
        if (!el.isConnected || (source === 'hover' && !el.matches(':hover')))
          release()
      }, WATCH_INTERVAL)
    }
    const track = (target: EventTarget | null, from: Source) => {
      let el = target instanceof Element ? target.closest(TARGET) : null
      for (; el; el = el.parentElement?.closest(TARGET) ?? null) {
        if (el === current) return
        const text = tipOf(el)
        if (text) return begin(el, text, from)
      }
      if (from === 'hover' && source === 'focus' && current) return
      release()
    }

    const controller = new AbortController()
    const { signal } = controller
    document.addEventListener(
      'pointerover',
      (event) => {
        if (event.pointerType === 'mouse') track(event.target, 'hover')
      },
      { signal }
    )
    document.addEventListener(
      'focusin',
      (event) => {
        if (
          event.target instanceof Element &&
          event.target.matches(':focus-visible')
        )
          track(event.target, 'focus')
      },
      { signal }
    )
    document.addEventListener(
      'focusout',
      () => {
        if (source === 'focus') release()
      },
      { signal }
    )
    document.addEventListener('pointerdown', hide, { signal, capture: true })
    document.addEventListener('keydown', hide, { signal, capture: true })
    document.addEventListener(
      'scroll',
      (event) => {
        if (
          current &&
          (event.target === document ||
            (event.target instanceof Node && event.target.contains(current)))
        )
          hide()
      },
      { signal, capture: true, passive: true }
    )
    window.addEventListener('blur', hide, { signal })
    return () => {
      controller.abort()
      release()
    }
  }, [])

  return (
    <BaseTooltip.Root open={open} disableHoverablePopup>
      <BaseTooltip.Portal>
        <BaseTooltip.Positioner
          anchor={tip?.anchor}
          side={tip?.side}
          sideOffset={8}
          collisionPadding={8}
          className="pointer-events-none z-[5000300]"
        >
          <BaseTooltip.Popup role="tooltip" className={TOOLTIP_POPUP_CLASS}>
            {tip?.text}
          </BaseTooltip.Popup>
        </BaseTooltip.Positioner>
      </BaseTooltip.Portal>
    </BaseTooltip.Root>
  )
}
